import { useParams, useNavigate } from 'react-router-dom';
import {
  Form,
  Input,
  DatePicker,
  Button,
  Spin,
  message,
  Row,
  Col,
  Typography,
  Space,
  Tag,
  Image,
  Divider,
  Switch,
  Avatar,
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  DollarOutlined,
  EditOutlined,
  PhoneOutlined,
  MailOutlined,
  UserOutlined,
  EnvironmentOutlined,
  SafetyOutlined,
  WifiOutlined,
} from '@ant-design/icons';
import { useState } from 'react';
import { useWisecureQuery, useWisecureMutation, useInvalidateQueries } from '@wisecure/api-client/hooks';
import type { UserResponse, DocumentResponse } from '@wisecure/api-client';
import { queryKeys } from '@/api/query-keys';
import { PageHeader } from '@/components/PageHeader';
import { CopyIdButton } from '@/components/CopyIdButton';
import { ConfirmDeleteButton } from '@/components/ConfirmDeleteButton';
import { DocumentList } from '@/components/DocumentList';
import { WisecureCard, ImageUpload } from '@/components/base';
import { neutral, primary, success } from '@/theme';
import dayjs from 'dayjs';

const { Text } = Typography;

interface PaginatedDocuments {
  data: DocumentResponse[];
  total: number;
}

export function UserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const invalidate = useInvalidateQueries();
  const [form] = Form.useForm();
  const [isEditing, setIsEditing] = useState(false);
  const [isEditingClient, setIsEditingClient] = useState(false);
  const [isEditingSo, setIsEditingSo] = useState(false);
  const [clientForm] = Form.useForm();
  const [soForm] = Form.useForm();

  const { data: user, isLoading } = useWisecureQuery<UserResponse>(
    queryKeys.userDetail(id!),
    (client) => client.users.usersControllerFindOne({ id: id! }),
    { enabled: !!id },
  );

  const { data: docsData, isLoading: docsLoading } = useWisecureQuery<PaginatedDocuments>(
    queryKeys.documents('user', id!),
    (client) =>
      client.documents.documentsControllerFindAll({
        entityType: 'user',
        entityId: id,
      }) as Promise<PaginatedDocuments>,
    { enabled: !!id },
  );

  const updateMutation = useWisecureMutation(
    (client, values: Record<string, unknown>) =>
      client.users.usersControllerUpdate({ id: id!, requestBody: values }),
    {
      onSuccess: () => {
        message.success('Utilisateur sauvegardé');
        setIsEditing(false);
        invalidate(queryKeys.userDetail(id!));
      },
      onError: () => message.error("Erreur lors de la sauvegarde de l'utilisateur"),
    },
  );

  const deleteMutation = useWisecureMutation(
    (client) => client.users.usersControllerRemove({ id: id! }),
    {
      onSuccess: () => {
        message.success('Utilisateur supprimé');
        navigate('/users/validation');
      },
      onError: () => message.error("Erreur lors de la suppression de l'utilisateur"),
    },
  );

  const updateClientProfileMutation = useWisecureMutation(
    (client, values: Record<string, unknown>) =>
      client.users.usersControllerUpdateClientProfile({ requestBody: values }),
    {
      onSuccess: () => {
        message.success('Profil client sauvegardé');
        setIsEditingClient(false);
        invalidate(queryKeys.userDetail(id!));
      },
      onError: () => message.error('Erreur lors de la sauvegarde du profil client'),
    },
  );

  const updateSoProfileMutation = useWisecureMutation(
    (client, values: Record<string, unknown>) =>
      client.users.usersControllerUpdateSoProfile({ requestBody: values }),
    {
      onSuccess: () => {
        message.success('Profil propriétaire sauvegardé');
        setIsEditingSo(false);
        invalidate(queryKeys.userDetail(id!));
      },
      onError: () => message.error('Erreur lors de la sauvegarde du profil propriétaire'),
    },
  );

  const handleSave = () => {
    form.validateFields().then((values) => {
      const payload: Record<string, unknown> = {};
      if (values.firstName !== user?.firstName) payload.firstName = values.firstName;
      if (values.lastName !== user?.lastName) payload.lastName = values.lastName;
      if (values.birthDate) payload.birthDate = values.birthDate.toISOString();
      if (values.phoneCountryCode || values.phoneNumber) {
        const currentCountryCode = String(user?.phone?.countryCode ?? '');
        const currentNumber = String(user?.phone?.number ?? '');
        if (values.phoneCountryCode !== currentCountryCode || values.phoneNumber !== currentNumber) {
          payload.phone = { countryCode: values.phoneCountryCode, number: values.phoneNumber };
        }
      }
      if (values.avatar !== (user?.avatar ?? undefined)) {
        payload.avatar = values.avatar ?? null;
      }
      if (Object.keys(payload).length === 0) {
        message.info('Aucune modification détectée');
        return;
      }
      updateMutation.mutate(payload);
    });
  };

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 80 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!user) {
    return <Text>Utilisateur non trouvé</Text>;
  }

  const formattedPhone = user.phone
    ? `${String(user.phone.countryCode ?? '')}${String(user.phone.number ?? '')}`
    : null;

  return (
    <div>
      <PageHeader
        title={`${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || user.email}
        subtitle="Détail de l'utilisateur"
        extra={
          <>
            <CopyIdButton id={user.id} />
            <ConfirmDeleteButton onConfirm={() => deleteMutation.mutate()} loading={deleteMutation.isPending}>
              Supprimer l'utilisateur
            </ConfirmDeleteButton>
          </>
        }
      />

      <Row gutter={24}>
        <Col xs={24} lg={12}>
          <WisecureCard
            style={{ marginBottom: 24 }}
            title={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Informations</span>
                {!isEditing && (
                  <Button
                    type="text"
                    icon={<EditOutlined />}
                    onClick={() => setIsEditing(true)}
                    size="small"
                    style={{ color: primary[400] }}
                  >
                    Modifier
                  </Button>
                )}
              </div>
            }
          >
            {!isEditing ? (
              <div>
                {user.avatar && (
                  <div style={{ marginBottom: 20, textAlign: 'center' }}>
                    <Image
                      src={user.avatar}
                      alt="Avatar"
                      style={{ maxHeight: 120, borderRadius: 12, objectFit: 'cover' }}
                      preview
                      fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAACklEQVQI12NgAAAAAgAB4iG8MwAAAABJRU5ErkJggg=="
                    />
                  </div>
                )}

                <Row gutter={[16, 20]}>
                  <Col xs={24} sm={12}>
                    <div>
                      <Text style={{ color: neutral[300], fontSize: 12, display: 'block', marginBottom: 4 }}>Prénom</Text>
                      <Space size={8}>
                        <UserOutlined style={{ color: neutral[300], fontSize: 14 }} />
                        <Text style={{ fontWeight: 500 }}>{user.firstName ?? '—'}</Text>
                      </Space>
                    </div>
                  </Col>
                  <Col xs={24} sm={12}>
                    <div>
                      <Text style={{ color: neutral[300], fontSize: 12, display: 'block', marginBottom: 4 }}>Nom</Text>
                      <Text style={{ fontWeight: 500 }}>{user.lastName ?? '—'}</Text>
                    </div>
                  </Col>
                  <Col xs={24} sm={12}>
                    <div>
                      <Text style={{ color: neutral[300], fontSize: 12, display: 'block', marginBottom: 4 }}>Email</Text>
                      <Space size={8}>
                        <MailOutlined style={{ color: neutral[300], fontSize: 14 }} />
                        <Text style={{ fontWeight: 500 }}>{user.email}</Text>
                      </Space>
                    </div>
                  </Col>
                  <Col xs={24} sm={12}>
                    <div>
                      <Text style={{ color: neutral[300], fontSize: 12, display: 'block', marginBottom: 4 }}>Téléphone</Text>
                      <Space size={8}>
                        <PhoneOutlined style={{ color: neutral[300], fontSize: 14 }} />
                        <Text style={{ fontWeight: 500 }}>{formattedPhone ?? '—'}</Text>
                      </Space>
                    </div>
                  </Col>
                  <Col xs={24} sm={12}>
                    <div>
                      <Text style={{ color: neutral[300], fontSize: 12, display: 'block', marginBottom: 4 }}>Date de naissance</Text>
                      <Text style={{ fontWeight: 500 }}>
                        {user.birthDate
                          ? new Date(user.birthDate).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })
                          : '—'}
                      </Text>
                    </div>
                  </Col>
                  <Col xs={24} sm={12}>
                    <div>
                      <Text style={{ color: neutral[300], fontSize: 12, display: 'block', marginBottom: 4 }}>Rôle</Text>
                      <Tag>{user.role}</Tag>
                    </div>
                  </Col>
                  <Col xs={24} sm={12}>
                    <div>
                      <Text style={{ color: neutral[300], fontSize: 12, display: 'block', marginBottom: 4 }}>Email vérifié</Text>
                      <Tag color={user.emailVerified === 'VALIDATED' ? 'success' : 'default'}>{user.emailVerified}</Tag>
                    </div>
                  </Col>
                </Row>
              </div>
            ) : (
              <Form
                form={form}
                layout="vertical"
                initialValues={{
                  firstName: user.firstName ?? '',
                  lastName: user.lastName ?? '',
                  birthDate: user.birthDate ? dayjs(user.birthDate) : undefined,
                  phoneCountryCode: String(user?.phone?.countryCode ?? ''),
                  phoneNumber: String(user?.phone?.number ?? ''),
                  avatar: user.avatar ?? undefined,
                }}
              >
                <Form.Item name="avatar" label="Avatar">
                  <ImageUpload folder="avatars" />
                </Form.Item>

                <Row gutter={16}>
                  <Col xs={24} sm={12}>
                    <Form.Item name="firstName" label="Prénom" rules={[{ required: true, message: 'Champ requis' }]}>
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item name="lastName" label="Nom" rules={[{ required: true, message: 'Champ requis' }]}>
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col xs={8} sm={6}>
                    <Form.Item name="phoneCountryCode" label="Indicatif">
                      <Input placeholder="+33" />
                    </Form.Item>
                  </Col>
                  <Col xs={16} sm={18}>
                    <Form.Item name="phoneNumber" label="Numéro">
                      <Input placeholder="612345678" />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item name="birthDate" label="Date de naissance">
                  <DatePicker style={{ width: '100%' }} />
                </Form.Item>

                <Divider style={{ margin: '8px 0 16px' }} />
                <Space>
                  <Button
                    type="primary"
                    onClick={handleSave}
                    loading={updateMutation.isPending}
                    disabled={updateMutation.isPending}
                  >
                    Sauvegarder
                  </Button>
                  <Button
                    disabled={updateMutation.isPending}
                    onClick={() => {
                      setIsEditing(false);
                      form.resetFields();
                    }}
                  >
                    Annuler
                  </Button>
                </Space>
              </Form>
            )}
          </WisecureCard>
        </Col>

        <Col xs={24} lg={12}>
          <WisecureCard title="Documents" style={{ marginBottom: 24 }}>
            <DocumentList
              documents={((docsData?.data ?? []) as unknown as import('@/components/DocumentList').DocumentItem[])}
              isLoading={docsLoading}
              entityType="user"
              entityId={id!}
            />
          </WisecureCard>
        </Col>
      </Row>

      {user.role === 'CLIENT' && user.clientProfile && (
        <WisecureCard
          style={{ marginBottom: 24 }}
          title={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Space>
                <UserOutlined style={{ color: primary[400] }} />
                <span>Profil Client</span>
              </Space>
              {!isEditingClient && (
                <Button
                  type="text"
                  icon={<EditOutlined />}
                  onClick={() => setIsEditingClient(true)}
                  size="small"
                  style={{ color: primary[400] }}
                >
                  Modifier
                </Button>
              )}
            </div>
          }
        >
          {!isEditingClient ? (
            <Row gutter={[16, 20]}>
              <Col xs={24} sm={12}>
                <div>
                  <Text style={{ color: neutral[300], fontSize: 12, display: 'block', marginBottom: 4 }}>
                    Alertes Wizz
                  </Text>
                  <Space size={8}>
                    <WifiOutlined style={{ color: user.clientProfile.wizzAlerte ? success[400] : neutral[300], fontSize: 14 }} />
                    <Tag color={user.clientProfile.wizzAlerte ? 'success' : 'default'}>
                      {user.clientProfile.wizzAlerte ? 'Activé' : 'Désactivé'}
                    </Tag>
                  </Space>
                </div>
              </Col>
              <Col xs={24} sm={12}>
                <div>
                  <Text style={{ color: neutral[300], fontSize: 12, display: 'block', marginBottom: 4 }}>
                    Code parrainage
                  </Text>
                  <Text style={{ fontWeight: 500, fontFamily: 'monospace' }}>
                    {user.clientProfile.sponsorshipCode ?? '—'}
                  </Text>
                </div>
              </Col>
              {user.clientProfile.favoriteInstaller && (
                <Col xs={24} sm={12}>
                  <div>
                    <Text style={{ color: neutral[300], fontSize: 12, display: 'block', marginBottom: 4 }}>
                      Installateur favori
                    </Text>
                    <Space size={8}>
                      {user.clientProfile.favoriteInstaller.logo && (
                        <Avatar src={user.clientProfile.favoriteInstaller.logo} size={24} />
                      )}
                      <Text style={{ fontWeight: 500 }}>
                        {user.clientProfile.favoriteInstaller.name}
                      </Text>
                    </Space>
                  </div>
                </Col>
              )}
            </Row>
          ) : (
            <Form
              form={clientForm}
              layout="vertical"
              initialValues={{
                wizzAlerte: user.clientProfile.wizzAlerte ?? false,
                sponsorshipCode: user.clientProfile.sponsorshipCode ?? '',
              }}
            >
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item name="wizzAlerte" label="Alertes Wizz" valuePropName="checked">
                    <Switch />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item name="sponsorshipCode" label="Code parrainage">
                    <Input />
                  </Form.Item>
                </Col>
              </Row>
              <Divider style={{ margin: '8px 0 16px' }} />
              <Space>
                <Button
                  type="primary"
                  onClick={() => {
                    clientForm.validateFields().then((values) => {
                      updateClientProfileMutation.mutate(values);
                    });
                  }}
                  loading={updateClientProfileMutation.isPending}
                  disabled={updateClientProfileMutation.isPending}
                >
                  Sauvegarder
                </Button>
                <Button
                  disabled={updateClientProfileMutation.isPending}
                  onClick={() => {
                    setIsEditingClient(false);
                    clientForm.resetFields();
                  }}
                >
                  Annuler
                </Button>
              </Space>
            </Form>
          )}
        </WisecureCard>
      )}

      {user.role === 'SOCIETY_OWNER' && user.societyOwnerProfile && (
        <WisecureCard
          style={{ marginBottom: 24 }}
          title={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Space>
                <SafetyOutlined style={{ color: primary[400] }} />
                <span>Profil Propriétaire</span>
              </Space>
              {!isEditingSo && (
                <Button
                  type="text"
                  icon={<EditOutlined />}
                  onClick={() => setIsEditingSo(true)}
                  size="small"
                  style={{ color: primary[400] }}
                >
                  Modifier
                </Button>
              )}
            </div>
          }
        >
          {!isEditingSo ? (
            <div>
              <Row gutter={[16, 20]}>
                <Col xs={24} sm={12}>
                  <div>
                    <Text style={{ color: neutral[300], fontSize: 12, display: 'block', marginBottom: 4 }}>
                      Ville de naissance
                    </Text>
                    <Text style={{ fontWeight: 500 }}>{user.societyOwnerProfile.birthCity ?? '—'}</Text>
                  </div>
                </Col>
                <Col xs={24} sm={12}>
                  <div>
                    <Text style={{ color: neutral[300], fontSize: 12, display: 'block', marginBottom: 4 }}>
                      Pays de naissance
                    </Text>
                    <Tag>{user.societyOwnerProfile.birthCountryCode ?? '—'}</Tag>
                  </div>
                </Col>
                <Col xs={24} sm={12}>
                  <div>
                    <Text style={{ color: neutral[300], fontSize: 12, display: 'block', marginBottom: 4 }}>
                      Nationalité
                    </Text>
                    <Tag>{user.societyOwnerProfile.nationalityCountryCode ?? '—'}</Tag>
                  </div>
                </Col>
              </Row>
              {user.societyOwnerProfile.address && (
                <>
                  <Divider style={{ margin: '20px 0' }} />
                  <div>
                    <Text style={{ color: neutral[300], fontSize: 12, display: 'block', marginBottom: 8 }}>
                      <EnvironmentOutlined style={{ marginRight: 6 }} />
                      Adresse
                    </Text>
                    <Text style={{ fontWeight: 500 }}>
                      {[user.societyOwnerProfile.address.number, user.societyOwnerProfile.address.street].filter(Boolean).join(' ')}
                      {[user.societyOwnerProfile.address.zip, user.societyOwnerProfile.address.city].filter(Boolean).join(' ')}
                      {user.societyOwnerProfile.address.country ? ` (${user.societyOwnerProfile.address.country})` : ''}
                    </Text>
                  </div>
                </>
              )}
            </div>
          ) : (
            <Form
              form={soForm}
              layout="vertical"
              initialValues={{
                birthCity: user.societyOwnerProfile.birthCity ?? '',
                birthCountryCode: user.societyOwnerProfile.birthCountryCode ?? '',
                nationalityCountryCode: user.societyOwnerProfile.nationalityCountryCode ?? '',
                addrNumber: user.societyOwnerProfile.address?.number ?? '',
                addrStreet: user.societyOwnerProfile.address?.street ?? '',
                addrCity: user.societyOwnerProfile.address?.city ?? '',
                addrZip: user.societyOwnerProfile.address?.zip ?? '',
                addrCountry: user.societyOwnerProfile.address?.country ?? '',
              }}
            >
              <Divider style={{ margin: '0 0 16px' }} />
              <Text style={{ color: neutral[500], fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 12 }}>
                Adresse
              </Text>
              <Row gutter={16}>
                <Col xs={8} sm={6}>
                  <Form.Item name="addrNumber" label="Numéro">
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={16} sm={18}>
                  <Form.Item name="addrStreet" label="Rue">
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={8} sm={8}>
                  <Form.Item name="addrZip" label="Code postal">
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={16} sm={10}>
                  <Form.Item name="addrCity" label="Ville">
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={6}>
                  <Form.Item
                    name="addrCountry"
                    label="Pays"
                    rules={[{ pattern: /^[a-zA-Z]{2}$/, message: '2 lettres' }]}
                  >
                    <Input maxLength={2} placeholder="FR" />
                  </Form.Item>
                </Col>
              </Row>

              <Divider style={{ margin: '8px 0 16px' }} />
              <Text style={{ color: neutral[500], fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 12 }}>
                Naissance & Nationalité
              </Text>
              <Row gutter={16}>
                <Col xs={24} sm={8}>
                  <Form.Item name="birthCity" label="Ville de naissance">
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={12} sm={8}>
                  <Form.Item
                    name="birthCountryCode"
                    label="Pays de naissance"
                    rules={[{ pattern: /^[a-zA-Z]{2}$/, message: '2 lettres' }]}
                  >
                    <Input maxLength={2} placeholder="FR" />
                  </Form.Item>
                </Col>
                <Col xs={12} sm={8}>
                  <Form.Item
                    name="nationalityCountryCode"
                    label="Nationalité"
                    rules={[{ pattern: /^[a-zA-Z]{2}$/, message: '2 lettres' }]}
                  >
                    <Input maxLength={2} placeholder="FR" />
                  </Form.Item>
                </Col>
              </Row>

              <Divider style={{ margin: '8px 0 16px' }} />
              <Space>
                <Button
                  type="primary"
                  onClick={() => {
                    soForm.validateFields().then((values) => {
                      const payload: Record<string, unknown> = {};
                      if (values.birthCity) payload.birthCity = values.birthCity;
                      if (values.birthCountryCode) payload.birthCountryCode = values.birthCountryCode;
                      if (values.nationalityCountryCode) payload.nationalityCountryCode = values.nationalityCountryCode;
                      if (values.addrNumber || values.addrStreet || values.addrCity || values.addrZip || values.addrCountry) {
                        payload.address = {
                          number: values.addrNumber,
                          street: values.addrStreet,
                          city: values.addrCity,
                          zip: values.addrZip,
                          country: values.addrCountry,
                        };
                      }
                      updateSoProfileMutation.mutate(payload);
                    });
                  }}
                  loading={updateSoProfileMutation.isPending}
                  disabled={updateSoProfileMutation.isPending}
                >
                  Sauvegarder
                </Button>
                <Button
                  disabled={updateSoProfileMutation.isPending}
                  onClick={() => {
                    setIsEditingSo(false);
                    soForm.resetFields();
                  }}
                >
                  Annuler
                </Button>
              </Space>
            </Form>
          )}
        </WisecureCard>
      )}

      {user.role === 'AGENT' && user.agentProfile && (
        <WisecureCard
          style={{ marginBottom: 24 }}
          title={
            <Space>
              <SafetyOutlined style={{ color: primary[400] }} />
              <span>Profil Agent</span>
            </Space>
          }
        >
          <Row gutter={[16, 20]}>
            <Col xs={24} sm={12}>
              <div>
                <Text style={{ color: neutral[300], fontSize: 12, display: 'block', marginBottom: 4 }}>Nom complet</Text>
                <Space size={8}>
                  {user.agentProfile.avatar && (
                    <Avatar src={user.agentProfile.avatar} size={28} />
                  )}
                  <Text style={{ fontWeight: 500 }}>
                    {user.agentProfile.firstName} {user.agentProfile.lastName}
                  </Text>
                </Space>
              </div>
            </Col>
            <Col xs={24} sm={12}>
              <div>
                <Text style={{ color: neutral[300], fontSize: 12, display: 'block', marginBottom: 4 }}>Email</Text>
                <Space size={8}>
                  <MailOutlined style={{ color: neutral[300], fontSize: 14 }} />
                  <Text style={{ fontWeight: 500 }}>{user.agentProfile.email}</Text>
                </Space>
              </div>
            </Col>
            {user.agentProfile.phone && (
              <Col xs={24} sm={12}>
                <div>
                  <Text style={{ color: neutral[300], fontSize: 12, display: 'block', marginBottom: 4 }}>Téléphone</Text>
                  <Space size={8}>
                    <PhoneOutlined style={{ color: neutral[300], fontSize: 14 }} />
                    <Text style={{ fontWeight: 500 }}>
                      {String(user.agentProfile.phone.countryCode ?? '')}{String(user.agentProfile.phone.number ?? '')}
                    </Text>
                  </Space>
                </div>
              </Col>
            )}
            <Col xs={24} sm={12}>
              <div>
                <Text style={{ color: neutral[300], fontSize: 12, display: 'block', marginBottom: 4 }}>Statut</Text>
                <Tag color={
                  user.agentProfile.status === 'OPERATIONAL' ? 'success' :
                  user.agentProfile.status === 'SUSPENDED' ? 'error' : 'default'
                }>
                  {user.agentProfile.status}
                </Tag>
              </div>
            </Col>
            <Col xs={24} sm={12}>
              <div>
                <Text style={{ color: neutral[300], fontSize: 12, display: 'block', marginBottom: 4 }}>En ligne</Text>
                <Space size={4}>
                  <div
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: user.agentProfile.isOnline ? success[400] : neutral[300],
                    }}
                  />
                  <Text style={{ fontWeight: 500 }}>{user.agentProfile.isOnline ? 'Oui' : 'Non'}</Text>
                </Space>
              </div>
            </Col>
            {user.agentProfile.societyName && (
              <Col xs={24} sm={12}>
                <div>
                  <Text style={{ color: neutral[300], fontSize: 12, display: 'block', marginBottom: 4 }}>Société</Text>
                  <Text style={{ fontWeight: 500 }}>{user.agentProfile.societyName}</Text>
                </div>
              </Col>
            )}
          </Row>
        </WisecureCard>
      )}

      {user.stripeAccount && (
        <WisecureCard
          title={
            <Space>
              <DollarOutlined style={{ color: '#635bff' }} />
              <span>Stripe</span>
            </Space>
          }
          style={{ marginBottom: 24 }}
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12}>
              <div>
                <Text style={{ color: neutral[300], fontSize: 12, display: 'block', marginBottom: 4 }}>Stripe ID</Text>
                <Typography.Text copyable style={{ fontFamily: 'monospace', fontSize: 12 }}>
                  {user.stripeAccount.stripeId}
                </Typography.Text>
              </div>
            </Col>
            {user.stripeAccount.currency && (
              <Col xs={24} sm={12}>
                <div>
                  <Text style={{ color: neutral[300], fontSize: 12, display: 'block', marginBottom: 4 }}>Devise</Text>
                  <Tag>{user.stripeAccount.currency.toUpperCase()}</Tag>
                </div>
              </Col>
            )}
            {user.stripeAccount.pendingBalance != null && (
              <Col xs={24} sm={12}>
                <div>
                  <Text style={{ color: neutral[300], fontSize: 12, display: 'block', marginBottom: 4 }}>Solde en attente</Text>
                  <Text style={{ fontWeight: 600 }}>
                    {(user.stripeAccount.pendingBalance / 100).toFixed(2)} {user.stripeAccount.currency?.toUpperCase() ?? ''}
                  </Text>
                </div>
              </Col>
            )}
            {user.stripeAccount.availableBalance != null && (
              <Col xs={24} sm={12}>
                <div>
                  <Text style={{ color: neutral[300], fontSize: 12, display: 'block', marginBottom: 4 }}>Solde disponible</Text>
                  <Text style={{ fontWeight: 600 }}>
                    {(user.stripeAccount.availableBalance / 100).toFixed(2)} {user.stripeAccount.currency?.toUpperCase() ?? ''}
                  </Text>
                </div>
              </Col>
            )}
            <Col xs={24} sm={12}>
              <div>
                <Text style={{ color: neutral[300], fontSize: 12, display: 'block', marginBottom: 4 }}>Paiements acceptés</Text>
                {user.stripeAccount.chargesEnabled ? (
                  <Space size={4}>
                    <CheckCircleOutlined style={{ color: '#52c41a' }} />
                    <Text style={{ fontWeight: 500 }}>Oui</Text>
                  </Space>
                ) : (
                  <Space size={4}>
                    <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                    <Text style={{ fontWeight: 500 }}>Non</Text>
                  </Space>
                )}
              </div>
            </Col>
            <Col xs={24} sm={12}>
              <div>
                <Text style={{ color: neutral[300], fontSize: 12, display: 'block', marginBottom: 4 }}>Retraits activés</Text>
                {user.stripeAccount.payoutsEnabled ? (
                  <Space size={4}>
                    <CheckCircleOutlined style={{ color: '#52c41a' }} />
                    <Text style={{ fontWeight: 500 }}>Oui</Text>
                  </Space>
                ) : (
                  <Space size={4}>
                    <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                    <Text style={{ fontWeight: 500 }}>Non</Text>
                  </Space>
                )}
              </div>
            </Col>
            {user.stripeAccount.detailsSubmitted != null && (
              <Col xs={24} sm={12}>
                <div>
                  <Text style={{ color: neutral[300], fontSize: 12, display: 'block', marginBottom: 4 }}>Détails soumis</Text>
                  <Text style={{ fontWeight: 500 }}>{user.stripeAccount.detailsSubmitted ? 'Oui' : 'Non'}</Text>
                </div>
              </Col>
            )}
            {user.stripeAccount.isActive != null && (
              <Col xs={24} sm={12}>
                <div>
                  <Text style={{ color: neutral[300], fontSize: 12, display: 'block', marginBottom: 4 }}>Statut</Text>
                  <Tag color={user.stripeAccount.isActive ? 'success' : 'error'}>
                    {user.stripeAccount.isActive ? 'Actif' : 'Inactif'}
                  </Tag>
                </div>
              </Col>
            )}
          </Row>
        </WisecureCard>
      )}
    </div>
  );
}
